import RefreshIcon from '@material-design-icons/svg/filled/refresh.svg?react';
import RestoreIcon from '@material-design-icons/svg/filled/restore.svg?react';
import SearchIcon from '@material-design-icons/svg/filled/search.svg?react';
import { use, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { DataTable } from '../components/common/DataTable';
import { Pagination } from '../components/common/Pagination';
import { Button } from '../components/ui/button';
import { TRPCContext } from '../contexts/TRPC';
import { AuthStateType, useAuth } from '../hooks/useAuth';
import { usePageTitle } from '../hooks/usePageTitle';
import { isLeftResult, isRightResult } from '../lib/trpcTypeGuards';
import {
  type QueryLoadContext,
  usePaginatedQueryLoader,
} from '../hooks/usePaginatedQueryLoader';
import { captureAndReportError } from '../utils/errorReporting';
import { hasSolanaEmailAddress } from '../utils/roleUtils';
import { formatDate } from '../utils/stringHelpers';

interface DeletedDappDetail {
  id: string;
  dappName: string;
  packageName: string | null;
  deletedAt: string;
  deletedBy: {
    id: string;
    email: string;
    fullName: string;
  } | null;
  deletionReason: string;
  publisherName: string;
  deletedReleasesCount: number;
  canRestore: boolean;
  restoreBlockedReason: string | null;
}

interface RestoreModalProps {
  dapp: DeletedDappDetail | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (restoreReleases: boolean) => void;
  isRestoring: boolean;
}

function RestoreModal({
  dapp,
  isOpen,
  onClose,
  onConfirm,
  isRestoring,
}: RestoreModalProps) {
  const [restoreReleases, setRestoreReleases] = useState(false);

  useEffect(() => {
    setRestoreReleases(false);
  }, [dapp?.id]);

  if (!isOpen || !dapp) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-neutral-800 rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-white mb-4">Restore Dapp</h3>
        <p className="text-sm text-neutral-400 mb-4">
          Are you sure you want to restore{' '}
          <span className="text-white font-medium">{dapp.dappName}</span>?
        </p>

        {dapp.deletedReleasesCount > 0 && (
          <div className="mb-4 p-3 bg-neutral-700 rounded-lg">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                aria-label={`Also restore ${dapp.deletedReleasesCount} deleted release(s)`}
                checked={restoreReleases}
                onChange={(e) => setRestoreReleases(e.target.checked)}
                className="mt-1"
              />
              <div>
                <div className="text-white font-medium">
                  Also restore {dapp.deletedReleasesCount} deleted release(s)
                </div>
                <div className="text-xs text-neutral-400">
                  Only releases deleted in the same dapp deletion are eligible
                </div>
              </div>
            </label>
          </div>
        )}

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isRestoring}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(restoreReleases)}
            disabled={isRestoring}
            className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
          >
            {isRestoring ? 'Restoring...' : 'Restore Dapp'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function DeletedDappsPage() {
  const [authState] = useAuth();
  const navigate = useNavigate();
  const trpc = use(TRPCContext);
  usePageTitle('Deleted Dapps | Publishing Portal');

  const [dapps, setDapps] = useState<DeletedDappDetail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [restoreModalDapp, setRestoreModalDapp] =
    useState<DeletedDappDetail | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);

  useEffect(() => {
    if (authState.type === AuthStateType.NoToken) {
      navigate('/auth');
    } else if (
      authState.type === AuthStateType.Authenticated &&
      authState.user.role !== 'superadmin'
    ) {
      navigate('/dashboard');
    }
  }, [authState, navigate]);

  const loadDeletedDapps = useCallback(
    async ({ isCurrentRequest }: QueryLoadContext) => {
      try {
        const result = await trpc.client.listDeletedDapps.query({
          search: searchQuery || undefined,
          pagination: {
            page: currentPage,
            limit: 20,
          },
        });

        if (!isCurrentRequest()) {
          return;
        }

        if (isRightResult(result)) {
          setDapps(result.right.dapps);
          setTotalPages(result.right.pagination.totalPages);
          setTotal(result.right.pagination.total);
        } else if (isLeftResult(result)) {
          toast.error(result.left.message);
        }
      } catch (error) {
        if (!isCurrentRequest()) {
          return;
        }

        captureAndReportError(error, {
          operation: 'admin.loadDeletedDapps',
          component: 'DeletedDappsPage',
        });
        toast.error('Failed to load deleted dapps');
      }
    },
    [currentPage, searchQuery, trpc.client],
  );

  const canAccessPage =
    authState.type === AuthStateType.Authenticated &&
    authState.user.role === 'superadmin' &&
    hasSolanaEmailAddress(authState.user.email);

  const { reload: reloadDeletedDapps } = usePaginatedQueryLoader({
    enabled: canAccessPage,
    setLoading: setIsLoading,
    loadPage: loadDeletedDapps,
  });

  const handleRestore = async (restoreReleases: boolean) => {
    if (!restoreModalDapp) return;

    setIsRestoring(true);
    try {
      const result = await trpc.client.restoreDapp.mutate({
        dappId: restoreModalDapp.id,
        restoreReleases,
      });

      if (isRightResult(result)) {
        toast.success(result.right.message);
        setRestoreModalDapp(null);
        await reloadDeletedDapps();
      } else if (isLeftResult(result)) {
        toast.error(result.left.message);
      }
    } catch (error) {
      captureAndReportError(error, {
        operation: 'admin.restoreDapp',
        component: 'DeletedDappsPage',
        dappId: restoreModalDapp.id,
      });
      toast.error('Failed to restore dapp');
    } finally {
      setIsRestoring(false);
    }
  };

  if (!canAccessPage) {
    return null;
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <div className="relative max-w-md">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 fill-current text-neutral-400 size-5" />
              <input
                type="text"
                aria-label="Search deleted dapps"
                placeholder="Search deleted dapps..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:border-teal-500"
              />
            </div>
          </div>

          <div className="mb-6 p-4 bg-neutral-800/50 border border-neutral-700 rounded-lg">
            <div className="flex items-start gap-3">
              <RefreshIcon className="fill-current text-teal-400 size-5 mt-0.5" />
              <div>
                <h3 className="text-white font-medium mb-1">
                  Soft Delete Recovery
                </h3>
                <p className="text-sm text-neutral-400">
                  These dapps remain in the database for recovery. Only dapps
                  deleted from the Dapps page can be restored here; records
                  deleted by publisher or user cleanup stay deleted.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-neutral-800/50 rounded-2xl border border-neutral-700 overflow-hidden">
            <div className="px-6 py-5 border-b border-neutral-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">
                  Deleted Dapps ({total})
                </h3>
                <div className="text-sm text-neutral-400">
                  Page {currentPage} of {totalPages || 1}
                </div>
              </div>
            </div>

            <DataTable
              data={dapps}
              columns={[
                {
                  key: 'dapp',
                  header: 'Dapp',
                  accessor: (dapp) => (
                    <div>
                      <div className="text-white font-medium">
                        {dapp.dappName}
                      </div>
                      <div className="text-xs text-neutral-400">
                        {dapp.packageName || 'No package'}
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'publisher',
                  header: 'Publisher',
                  accessor: (dapp) => (
                    <span className="text-sm text-white">
                      {dapp.publisherName}
                    </span>
                  ),
                },
                {
                  key: 'deletedBy',
                  header: 'Deleted By',
                  accessor: (dapp) => (
                    <div>
                      {dapp.deletedBy ? (
                        <>
                          <div className="text-sm text-white">
                            {dapp.deletedBy.fullName}
                          </div>
                          <div className="text-xs text-neutral-400">
                            {dapp.deletedBy.email}
                          </div>
                        </>
                      ) : (
                        <span className="text-sm text-neutral-400">System</span>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'deletedAt',
                  header: 'Deleted At',
                  accessor: (dapp) => (
                    <span className="text-sm text-neutral-400">
                      {formatDate(
                        dapp.deletedAt,
                        {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        },
                        {
                          formatter: 'datetime',
                        },
                      )}
                    </span>
                  ),
                },
                {
                  key: 'reason',
                  header: 'Reason',
                  accessor: (dapp) => (
                    <span className="text-sm text-neutral-400">
                      {dapp.deletionReason}
                    </span>
                  ),
                },
                {
                  key: 'releases',
                  header: 'Deleted Releases',
                  accessor: (dapp) => (
                    <span className="text-sm text-white">
                      {dapp.deletedReleasesCount}
                    </span>
                  ),
                },
                {
                  key: 'actions',
                  header: 'Actions',
                  accessor: (dapp) => (
                    <button
                      type="button"
                      onClick={() => {
                        if (dapp.canRestore) setRestoreModalDapp(dapp);
                      }}
                      disabled={!dapp.canRestore}
                      className="text-teal-400 transition-colors hover:text-teal-300 disabled:cursor-not-allowed disabled:text-neutral-600"
                      title={dapp.restoreBlockedReason ?? 'Restore dapp'}
                    >
                      <RestoreIcon className="fill-current size-4" />
                    </button>
                  ),
                },
              ]}
              isLoading={isLoading}
              emptyMessage="No deleted dapps found"
              renderMobileItem={(dapp) => (
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-sm font-medium text-white">
                        {dapp.dappName}
                      </div>
                      <div className="text-xs text-neutral-400">
                        {dapp.packageName || 'No package'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (dapp.canRestore) setRestoreModalDapp(dapp);
                      }}
                      disabled={!dapp.canRestore}
                      className="p-1 text-teal-400 hover:text-teal-300 disabled:cursor-not-allowed disabled:text-neutral-600"
                      title={dapp.restoreBlockedReason ?? 'Restore dapp'}
                    >
                      <RestoreIcon className="fill-current size-5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-neutral-400">
                    <div>
                      <span className="text-neutral-500">Deleted:</span>{' '}
                      <span className="text-neutral-300">
                        {formatDate(
                          dapp.deletedAt,
                          {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          },
                          {
                            formatter: 'datetime',
                          },
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="text-neutral-500">Releases:</span>{' '}
                      <span className="text-neutral-300">
                        {dapp.deletedReleasesCount}
                      </span>
                    </div>
                  </div>

                  <div className="text-xs text-neutral-400">
                    <div className="text-neutral-500 mb-0.5">Reason:</div>
                    <div className="italic text-neutral-300">
                      {dapp.deletionReason}
                    </div>
                  </div>

                  <div className="text-xs text-neutral-400 border-t border-neutral-700/50 pt-2 flex justify-between">
                    <span>
                      By: {dapp.deletedBy ? dapp.deletedBy.fullName : 'System'}
                    </span>
                  </div>
                </div>
              )}
            />

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>

      <RestoreModal
        dapp={restoreModalDapp}
        isOpen={!!restoreModalDapp}
        onClose={() => setRestoreModalDapp(null)}
        onConfirm={handleRestore}
        isRestoring={isRestoring}
      />
    </>
  );
}
